import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web/common/media";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { isNotNull } from "@faire/web/common/typescriptUtils";
import classNames from "classnames";
import React from "react";

import { EAEAEA_PIXEL } from "@faire/retailer/consts/EAEAEA_PIXEL";

import * as styles from "./styles.css";

type Props = {
  banner_image: IImage | undefined;
  profile_image: IImage | undefined;
  $ref?: React.Ref<HTMLDivElement>;
  isBrandPageRedesign?: boolean;
};

export const Banner: React.FC<Props> = ({
  banner_image,
  profile_image,
  $ref,
  isBrandPageRedesign,
}) => {
  const bannerMediaQueries = banner_image ? getMediaQueries(banner_image) : [];

  return (
    <div className={styles.container} ref={$ref}>
      <picture>
        {bannerMediaQueries.map(({ key, srcSet, media }) => {
          return <source key={key} srcSet={srcSet} media={media} />;
        })}
        <img
          // React creates an HTMLImageElement and then later adds it to the document.
          // This means that in Firefox, if we set this to a real image, it will start loading.
          // Firefox sometimes cancels the download once the img is added to the picture, but
          // not always.
          className={styles.bannerImage}
          src={EAEAEA_PIXEL}
          alt="Brand cover"
        />
      </picture>
      <img
        src={
          profile_image
            ? optimizeImageUrl(profile_image, true, {
                width: styles.PROFILE_IMAGE_SIZE,
                height: styles.PROFILE_IMAGE_SIZE,
                resizeMethod: "fill",
                serverOnly: true,
              })
            : EAEAEA_PIXEL
        }
        className={classNames(
          styles.profileImage,
          isBrandPageRedesign ? styles.leftAlignedProfile : null
        )}
        width={styles.PROFILE_IMAGE_SIZE}
        height={styles.PROFILE_IMAGE_SIZE}
      />
    </div>
  );
};

// exported to test
// ts-prune-ignore-next
export function getMediaQueries(image: IImage) {
  const BANNER_WIDTHS = [
    // From https://worship.agency/mobile-screen-sizes-for-2022-based-on-data-from-2021
    320,
    360,
    375,
    390,
    412,
    414,
    428,
    // Not a common device size, but there's a bit of a gap between 428 and 768.
    600,
    // From https://mediag.com/blog/popular-screen-resolutions-designing-for-all/
    MIN_WIDTH_BREAKPOINTS.TABLET,
    // Not a common device size, but there's a bit of a gap between 768 and 1023.
    900,
    styles.BANNER_DIMENSIONS["desktop"].width - 1,
    styles.BANNER_DIMENSIONS["desktop"].width,
  ];
  BANNER_WIDTHS.sort((a, b) => a - b);

  const mediaQueries: Array<{ key: string; srcSet: string; media: string }> =
    [];

  BANNER_WIDTHS.forEach((width, i) => {
    const prevWidth = BANNER_WIDTHS[i - 1];
    const nextWidth = BANNER_WIDTHS[i + 1];
    const height =
      styles.BANNER_DIMENSIONS[
        width >= MIN_WIDTH_BREAKPOINTS.TABLET ? "desktop" : "mobile"
      ].height;

    const minWidth = prevWidth ? prevWidth + 1 : null;
    const maxWidth = nextWidth ? width : null;

    mediaQueries.push({
      key: `${i}-a`,
      srcSet: coverUrl(image, true, 2, width, height),
      media: buildMediaQuery(minWidth, maxWidth, true),
    });

    mediaQueries.push({
      key: `${i}-b`,
      srcSet: coverUrl(image, true, 1, width, height),
      media: buildMediaQuery(minWidth, maxWidth, false),
    });
  });

  return mediaQueries;
}

// exported to test
// ts-prune-ignore-next
export const buildMediaQuery = (
  minWidth: number | null,
  maxWidth: number | null,
  hidpi: boolean
) =>
  [
    minWidth ? `(min-width: ${minWidth}px)` : null,
    maxWidth ? `(max-width: ${maxWidth}px)` : null,
    hidpi ? "(min-resolution: 1.5dppx)" : null,
  ]
    .filter(isNotNull)
    .join(" and ");

const coverUrl = (
  cover: IImage | undefined,
  isSSR: boolean,
  dpr: number,
  width: number,
  height: number
): string => {
  if (!cover) {
    return EAEAEA_PIXEL;
  }
  return (
    optimizeImageUrl(cover, isSSR, {
      width,
      height,
      resizeMethod: "fill",
      dprOverride: dpr,
      serverOnly: true,
    }) ?? EAEAEA_PIXEL
  );
};
